import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Bed, 
  Users, 
  MapPin,
  ChevronRight
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import BookingWidget from '../components/BookingWidget';
import RichText from '../components/RichText';
import UnescoLink from '../components/UnescoLink';

const Villa = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="villa-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] bg-[#1E3A5F]">

        <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/40 to-[#1E3A5F]/80" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-12 pb-16">
            <p className="uppercase tracking-widest text-xs text-[#D4A72C] mb-4">
              {t('nav.villa')}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4">
              <span className="gold-shimmer">Villa Aristotle</span>
            </h1>
            <div className="flex items-center gap-6 text-white/70">
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5" />
                <span>{t('villa.bedrooms')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{t('villa.maxGuests')}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>{t('hero.subtitle')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-3">
              <div className="w-16 h-0.5 bg-[#D4A72C] mb-8" />
              <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A5F] mb-6">
                {t('villa.welcome')}
              </h2>
              <div className="prose prose-lg text-[#1E3A5F]/70 space-y-4">
                <p className="leading-relaxed">
                  <RichText text={t('villa.descPart1')} tokens={{ unesco: <UnescoLink textKey="villa.unescoLink" /> }} />
                </p>
                <p className="leading-relaxed">
                  {t('villa.descPart2')}
                </p>
                <p className="leading-relaxed">
                  {t('villa.descPart3')}
                </p>
              </div>
            </div>
            
            <div className="lg:col-span-2">
              <div className="bg-[#f8fafc] rounded-sm p-8 sticky top-28 border border-[#1E3A5F]/10">
                <h3 className="font-serif text-xl mb-6 text-[#1E3A5F]">{t('villa.quickEnquiry')}</h3>
                <p className="text-sm text-[#1E3A5F]/60 mb-6">
                  {t('villa.quickEnquiryText')}
                </p>
                <div className="pt-6 border-t border-[#1E3A5F]/10">
                  <Link to="/booking">
                    <Button
                      data-testid="villa-book-btn"
                      className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white btn-lift"
                    >
                      {t('booking.checkAvailability')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Villa Photos */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-sm overflow-hidden bg-[#1E3A5F]/10 h-[300px]">
              <img
                src="/images/exterior.jpg"
                alt={t('villa.exteriorPhoto')}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-sm overflow-hidden bg-[#1E3A5F]/10 h-[300px]">
              <img
                src="/images/livingroom.jpg"
                alt={t('villa.interiorPhoto')}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/bedrooms"
              data-testid="villa-link-bedrooms"
              className="group bg-white border border-[#1E3A5F]/10 rounded-sm p-8 hover:shadow-lg hover:border-[#D4A72C]/30 transition-all"
            >
              <h3 className="font-serif text-xl mb-2 text-[#1E3A5F] group-hover:text-[#D4A72C] transition-colors">
                {t('nav.bedrooms')}
              </h3>
              <p className="text-sm text-[#1E3A5F]/60 mb-4">
                {t('villa.viewAccommodation')}
              </p>
              <span className="flex items-center text-[#D4A72C] text-sm font-medium">
                {t('common.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
            
            <Link
              to="/amenities"
              data-testid="villa-link-amenities"
              className="group bg-white border border-[#1E3A5F]/10 rounded-sm p-8 hover:shadow-lg hover:border-[#D4A72C]/30 transition-all"
            >
              <h3 className="font-serif text-xl mb-2 text-[#1E3A5F] group-hover:text-[#D4A72C] transition-colors">
                {t('nav.amenities')}
              </h3>
              <p className="text-sm text-[#1E3A5F]/60 mb-4">
                {t('villa.discoverAvailable')}
              </p>
              <span className="flex items-center text-[#D4A72C] text-sm font-medium">
                {t('common.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
            
            <Link
              to="/location"
              data-testid="villa-link-location"
              className="group bg-white border border-[#1E3A5F]/10 rounded-sm p-8 hover:shadow-lg hover:border-[#D4A72C]/30 transition-all"
            >
              <h3 className="font-serif text-xl mb-2 text-[#1E3A5F] group-hover:text-[#D4A72C] transition-colors">
                {t('nav.location')}
              </h3>
              <p className="text-sm text-[#1E3A5F]/60 mb-4">
                {t('villa.exploreCorfu')}
              </p>
              <span className="flex items-center text-[#D4A72C] text-sm font-medium">
                {t('common.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky Mobile Booking */}
      <BookingWidget variant="sticky" />
    </div>
  );
};

export default Villa;
